import { useState } from 'react'
import { Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react'
import { Slider } from '@klear/ui/components/slider'
import { Modal } from '@klear/ui/components/modal'
import { useFileDownload } from '@klear/ui/lib/download-file'
import isNumber from 'lodash/isNumber'

interface ImgPreviewModalProps {
  fileUrl: string
  fileName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  downloadUrl?: string
}

export default function ImgPreviewModal({ fileUrl, fileName, open, onOpenChange, downloadUrl }: ImgPreviewModalProps) {
  const [error, setError] = useState<Error | null>(null)
  const [scale, setScale] = useState<number>(1.0)
  const { startDownload, isDownloading } = useFileDownload()

  if (!fileUrl) {
    return null
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={fileName}>
      <div className="w-full">
        {fileUrl && (
          <div className="flex justify-center mb-4">
            <button
              type="button"
              className="inline-flex items-center gap-2 px-3 py-1 bg-muted rounded hover:bg-muted/80 disabled:opacity-60"
              onClick={() => startDownload(downloadUrl ?? fileUrl, fileName)}
              disabled={isDownloading}
            >
              {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span>Download</span>
            </button>
          </div>
        )}
        <div className="border rounded-lg p-4 bg-card shadow-sm w-full h-[70vh] overflow-auto flex items-center justify-center">
          <img
            src={fileUrl}
            alt={fileName}
            className="object-contain max-w-full max-h-full transition-transform duration-200 origin-center"
            style={{
              transform: `scale(${scale})`
            }}
            onError={() => setError(new Error('Failed to load image'))}
          />
        </div>

        <div className="mt-4 flex items-center gap-3 w-1/2 mx-auto">
          <ZoomOut className="w-4 h-4 text-muted-foreground" />
          <Slider
            min={0.5}
            max={3}
            step={0.05}
            value={[scale]}
            onValueChange={([val]) => setScale(isNumber(val) ? val : scale)}
            aria-label="Zoom"
          />
          <ZoomIn className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm w-12 text-center font-medium">{Math.round(scale * 100)}%</span>
        </div>
        {error && <div className="text-red-500 mt-2">Failed to load Image: {error.message}</div>}
      </div>
    </Modal>
  )
}
